/** @jsx jsx */
import type { FC } from 'react';
import React, { memo, useCallback } from 'react';
import { styled, css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import AkSpinner from '@atlaskit/spinner/spinner';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import NodeIcon from '@atlaskit/icon/core/node';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import type { ContentTreeItem } from './data-transformers.ts';

const SPINNER_SIZE_MEDIUM = 16;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTreeItemSpinner = styled.div({
	margin: `0 ${token('space.050')}`,
});

export type BeforeIconProps = {
	item: ContentTreeItem;
	isSuperAdmin?: boolean;
	onExpand(e: React.MouseEvent): void;
	onCollapse(e: React.MouseEvent): void;
	isBlockedDropTarget?: boolean;
};

const beforeIconStyles = css({
	zIndex: 1,
	display: 'flex',
	border: 'none',
	margin: 0,
	padding: 0,
	textDecoration: 'none',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	height: token('space.300'),
	width: token('space.300'),
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	color: token('color.text.subtle'),
	transition: token('motion.button.hovered'),
});

const beforeIconMotionStyles = css({
	transition: token('motion.button.hovered'),
});

export const BeforeIcon: FC<BeforeIconProps> = memo(
	({ item, isSuperAdmin, onExpand, onCollapse, isBlockedDropTarget }) => {
		const {
			isChildrenLoading,
			hasChildren,
			isExpanded,
			hasRestrictions,
			hasInheritedRestrictions,
		} = item;

		const shouldDisplayRed = Boolean(
			!!isSuperAdmin && (hasRestrictions || hasInheritedRestrictions),
		);

		const handleOnExpand = useCallback(
			(event: React.MouseEvent) => {
				traceUFOPress('page-tree-item-expand', event.timeStamp);
				onExpand(event);
			},
			[onExpand],
		);

		const handleOnCollapse = useCallback(
			(event: React.MouseEvent) => {
				traceUFOPress('page-tree-item-collapse', event.timeStamp);
				onCollapse(event);
			},
			[onCollapse],
		);

		const iconColor = isBlockedDropTarget
			? token('color.icon.disabled')
			: shouldDisplayRed
				? token('color.icon.danger')
				: undefined;

		if (isChildrenLoading) {
			return (
				<PageTreeItemSpinner data-testid="tree-item-spinner">
					<AkSpinner size={SPINNER_SIZE_MEDIUM} />
				</PageTreeItemSpinner>
			);
		}

		if (hasChildren || item.data.type === 'folder') {
			return isExpanded ? (
				<button
					onClick={handleOnCollapse}
					data-testid="chevron-down"
					data-no-global-anchor-handler
					css={[beforeIconStyles, beforeIconMotionStyles]}
					aria-expanded={isExpanded}
					aria-labelledby={item.id}
				>
					<ChevronDownIcon color={iconColor} label="" size="small" />
				</button>
			) : (
				<button
					onClick={handleOnExpand}
					data-testid="chevron-right"
					data-no-global-anchor-handler
					css={[beforeIconStyles, beforeIconMotionStyles]}
					aria-expanded={isExpanded}
					aria-labelledby={item.id}
				>
					<ChevronRightIcon color={iconColor} label="" size="small" />
				</button>
			);
		}

		return (
			<NodeIcon
				label=""
				color={isBlockedDropTarget ? token('color.icon.disabled') : token('color.text.subtle')}
			/>
		);
	},
);
